/**
 * Passkey (WebAuthn) facade — the browser ceremony plus one-call
 * register / sign-in, so a consumer never has to touch
 * `navigator.credentials` or the base64url ⇄ ArrayBuffer wiring the
 * server (webauthn-rs) speaks.
 *
 *   import { createClient, passkeys } from "@nexys/user-management-client";
 *
 *   const um = createClient({ baseUrl });
 *   const pk = passkeys(um);
 *
 *   if (pk.supported()) {
 *     await pk.register({ name: "MacBook Touch ID" }); // signed-in user
 *     await pk.signIn();                                // discoverable
 *     await pk.signIn({ email });                       // scoped to a user
 *   }
 *
 * Every method returns the SDK's `Result` — nothing throws. Server
 * failures surface as the usual `ApiError` / `NetworkError`; browser
 * ceremony failures (unsupported, user cancelled) surface as a typed
 * {@link PasskeyError}, so a UI can tell "user hit cancel" from "the
 * server rejected it" without a try/catch.
 *
 * The ceremony is browser-only. In a non-browser runtime `supported()`
 * is `false` and `register` / `signIn` resolve to
 * `{ ok: false, error: { kind: "passkey", reason: "unsupported" } }`
 * rather than throwing — the rest of the SDK stays usable server-side.
 */
import type { UmClient } from "./client.js";
import { err, ok, type ApiError, type NetworkError, type Result } from "./result.js";
import type { JsonValue, OkResponse, PasskeyItem, PasskeySignInResult } from "./types.js";

export type PasskeyErrorReason =
  | "unsupported"
  | "cancelled"
  | "duplicate"
  | "ceremony_failed";

/**
 * A client-side WebAuthn failure — distinct from a server `ApiError`.
 * `unsupported`: no WebAuthn in this runtime. `cancelled`: the user
 * dismissed the platform prompt (or it timed out). `ceremony_failed`:
 * the authenticator/browser raised something else.
 */
export interface PasskeyError {
  kind: "passkey";
  reason: PasskeyErrorReason;
  message: string;
}

type Res<T> = Promise<Result<T, ApiError | NetworkError | PasskeyError>>;

// ── browser feature detection ────────────────────────────────────────

/** True when this runtime can run the WebAuthn ceremony. */
export const browserSupportsPasskeys = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential !== "undefined" &&
  typeof navigator !== "undefined" &&
  !!navigator.credentials;

/**
 * Whether the browser can surface passkeys in the input autofill
 * dropdown (conditional mediation). Resolves `false` when unsupported —
 * safe to call anywhere.
 */
export const passkeyAutofillAvailable = async (): Promise<boolean> => {
  if (!browserSupportsPasskeys()) return false;
  try {
    const pk = window.PublicKeyCredential as unknown as {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    };
    return (await pk.isConditionalMediationAvailable?.()) === true;
  } catch {
    return false;
  }
};

// ── base64url ⇄ ArrayBuffer ──────────────────────────────────────────
// The server encodes binary credential fields as base64url strings;
// `navigator.credentials` wants ArrayBuffers. Convert both ways.

const b64urlToBuf = (s: string): ArrayBuffer => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
};

const bufToB64url = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

type ServerOptions = Record<string, unknown>;

interface CredDescriptor {
  id: string;
  type: string;
  transports?: string[];
}

// ── low-level ceremony (escape hatch; most callers use the facade) ────

/**
 * Run `navigator.credentials.create()` from the server's registration
 * options JSON and serialise the attestation back to the JSON
 * `passkeyRegisterFinish` expects. Throws on cancellation/failure — the
 * {@link passkeys} facade wraps this into a `Result` for you.
 */
export const createPasskeyCredential = async (
  serverOptions: JsonValue | ServerOptions
): Promise<JsonValue> => {
  const pk = (serverOptions as ServerOptions).publicKey as ServerOptions;
  const options: PublicKeyCredentialCreationOptions = {
    ...(pk as unknown as PublicKeyCredentialCreationOptions),
    challenge: b64urlToBuf(pk.challenge as string),
    user: {
      ...(pk.user as PublicKeyCredentialUserEntity),
      id: b64urlToBuf((pk.user as { id: string }).id),
    },
    excludeCredentials: ((pk.excludeCredentials as CredDescriptor[]) ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })) as PublicKeyCredentialDescriptor[],
  };
  const cred = (await navigator.credentials.create({
    publicKey: options,
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey creation was cancelled.");
  const resp = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(resp.attestationObject),
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      // How the authenticator talks to this device (internal, hybrid,
      // usb, …) — the server stores it as passkey metadata. Optional
      // API, so older browsers simply omit it.
      transports: resp.getTransports?.() ?? undefined,
    },
    extensions: (cred.getClientExtensionResults?.() ?? {}) as JsonValue,
  };
};

/**
 * Run `navigator.credentials.get()` from the server's sign-in options
 * JSON and serialise the assertion back to the JSON
 * `passkeySignInFinish` expects. Pass `mediation: "conditional"` for
 * autofill UI. Throws on cancellation/failure.
 */
export const getPasskeyAssertion = async (
  serverOptions: JsonValue | ServerOptions,
  mediation?: CredentialMediationRequirement
): Promise<JsonValue> => {
  const pk = (serverOptions as ServerOptions).publicKey as ServerOptions;
  const options: PublicKeyCredentialRequestOptions = {
    ...(pk as unknown as PublicKeyCredentialRequestOptions),
    challenge: b64urlToBuf(pk.challenge as string),
    allowCredentials: ((pk.allowCredentials as CredDescriptor[]) ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })) as PublicKeyCredentialDescriptor[],
  };
  const cred = (await navigator.credentials.get({
    publicKey: options,
    ...(mediation ? { mediation } : {}),
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey sign-in was cancelled.");
  const resp = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(resp.authenticatorData),
      clientDataJSON: bufToB64url(resp.clientDataJSON),
      signature: bufToB64url(resp.signature),
      userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
    },
    extensions: (cred.getClientExtensionResults?.() ?? {}) as JsonValue,
  };
};

const unsupportedError = (): PasskeyError => ({
  kind: "passkey",
  reason: "unsupported",
  message: "This browser doesn't support passkeys.",
});

/** Map a thrown ceremony exception to a typed `PasskeyError`. */
const toPasskeyError = (e: unknown): PasskeyError => {
  // The browser raises NotAllowedError when the user dismisses the
  // prompt or it times out; AbortError when an in-flight request is
  // superseded (e.g. autofill + a click).
  const name = e instanceof Error ? e.name : "";
  if (name === "NotAllowedError" || name === "AbortError") {
    return { kind: "passkey", reason: "cancelled", message: "The passkey prompt was dismissed." };
  }
  // InvalidStateError: the authenticator already holds one of the
  // `excludeCredentials` — i.e. this device already has a passkey for
  // this site (often synced, possibly under another account).
  if (name === "InvalidStateError") {
    return {
      kind: "passkey",
      reason: "duplicate",
      message:
        "This device already holds a passkey for this site — possibly for " +
        "another account. Try signing in with it instead, or remove it from " +
        "your device's password manager before registering a new one.",
    };
  }
  return {
    kind: "passkey",
    reason: "ceremony_failed",
    message: e instanceof Error ? e.message : String(e),
  };
};

// ── high-level facade ────────────────────────────────────────────────

export interface RegisterPasskeyOptions {
  /** Human label shown in the user's passkey list (e.g. the device). */
  name?: string;
}

export interface SignInWithPasskeyOptions {
  /**
   * Scope the prompt to one user's authenticators. Omit for the
   * browser's discoverable-credential UX (the usual passwordless flow).
   */
  email?: string;
  /**
   * WebAuthn mediation. Pass `"conditional"` to drive autofill UI from
   * an `autocomplete="username webauthn"` field.
   */
  mediation?: CredentialMediationRequirement;
  /**
   * The product signing in (`UM_PROJECT_ID`): scopes the consent check
   * to its documents and stamps its id (`pid`) on the session's tokens.
   */
  projectId?: string;
}

export interface Passkeys {
  /** Whether this runtime can run the passkey ceremony. */
  supported(): boolean;
  /**
   * Register a passkey for the currently signed-in user (cookie auth):
   * `register/start` → `navigator.credentials.create()` →
   * `register/finish`. Returns the new passkey id.
   */
  register(options?: RegisterPasskeyOptions): Res<{ id: string }>;
  /**
   * Sign in with a passkey: `sign-in/start` →
   * `navigator.credentials.get()` → `sign-in/finish`. On success the
   * session cookie is set and the user is returned — unless the account
   * owes a blocking consent, in which case a `ConsentChallenge` comes
   * back instead (narrow with `isConsentChallenge`). Passkey sign-in
   * never yields a 2FA challenge; the authenticator is the second factor.
   */
  signIn(options?: SignInWithPasskeyOptions): Res<PasskeySignInResult>;
  /** The signed-in user's registered passkeys. */
  list(): Res<PasskeyItem[]>;
  /** Remove one of the signed-in user's passkeys by id. */
  remove(id: string): Res<OkResponse>;
}

/**
 * Bind the passkey flows to a client. Mirrors {@link tenantAdmin}: thin,
 * Result-returning, never throws.
 */
export const passkeys = (client: UmClient): Passkeys => ({
  supported: browserSupportsPasskeys,

  async register(options = {}) {
    if (!browserSupportsPasskeys()) return err(unsupportedError());
    const start = await client.passkeyRegisterStart();
    if (!start.ok) return start;
    let response: JsonValue;
    try {
      response = await createPasskeyCredential(start.data.options);
    } catch (e) {
      return err(toPasskeyError(e));
    }
    return client.passkeyRegisterFinish({
      challenge: start.data.challenge,
      response,
      name: options.name?.trim() || undefined,
    });
  },

  async signIn(options = {}) {
    if (!browserSupportsPasskeys()) return err(unsupportedError());
    const start = await client.passkeySignInStart({ email: options.email });
    if (!start.ok) return start;
    let response: JsonValue;
    try {
      response = await getPasskeyAssertion(start.data.options, options.mediation);
    } catch (e) {
      return err(toPasskeyError(e));
    }
    return client.passkeySignInFinish({
      challenge: start.data.challenge,
      response,
      projectId: options.projectId,
    });
  },

  async list() {
    const res = await client.listPasskeys();
    return res.ok ? ok(res.data.passkeys) : res;
  },

  remove(id) {
    return client.deleteOwnPasskey({ id });
  },
});
